import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Container, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { get, isEmpty } from 'lodash';
import DynamicForm from 'screens/common/dynamicForm';
import { useNavigate, useParams } from 'react-router-dom';
import { cultivationApi } from 'api/endpoints/Cultivation';
import { toast } from 'react-toastify';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';
import useOnlineStatus from 'hooks/useOnlineStatus';
import offlineDBService from 'db/dexieDB';

const CultivationForm = ({ edit, view = false }) => {
  const tenant = getTenantFromToken();
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [title, setTitle] = useState('');
  const { id } = useParams();
  const farmId = id; // The farm ID for create mode
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  const [cultivationData, setCultivationData] = useState(null);
  const isOnline = useOnlineStatus();

  // Helper function to process offline form fields according to requirements
  const processOfflineFormFields = fields => {
    if (!Array.isArray(fields)) {
      console.warn('Form fields is not an array:', fields);
      return fields;
    }

    // Fields to remove from the form
    const fieldsToRemove = [
      'farmer_name',
      'farm_name',
      'farm_area',
      'unutilized_area'
    ];

    // Filter out unwanted fields and make all editable fields non-editable
    return fields
      .filter(field => !fieldsToRemove.includes(field.id))
      .map(field => {
        let updatedField = { ...field };

        // Make all fields non-editable
        if (updatedField.isEditable === false) {
          updatedField.isEditable = true;
        }

        // Fix the cultivated_area field validation by removing maxValue or setting it to null
        if (updatedField.id === 'cultivated_area' && updatedField.validation) {
          updatedField.validation = {
            ...updatedField.validation,
            maxValue: undefined // Set maxValue to null
          };
        }

        return updatedField;
      });
  };

  const fetchFormData = async () => {
    setLoading(true);
    try {
      let formData;
      let formResponse;

      // Determine which action we're taking (create, edit, or view)
      if (!edit && !view) {
        // CREATE MODE
        if (isOnline) {
          // Online - fetch from API
          formResponse = await cultivationApi.getCultivationForm(
            farmId,
            false // edit:false
          );
          formData = formResponse.data.results;
          setTitle(formData?.form?.title || 'Create Cultivation');
          setOfflineMode(false);
        } else {
          // Offline - fetch from IndexedDB
          console.log('Device is offline, fetching form from local DB');
          const formDef = await offlineDBService.getFormDefinition(
            'cultivation-create'
          );

          if (formDef) {
            console.log('Form found in local DB:', formDef);

            // Handle the specific structure from IndexedDB
            // Extract formData if it exists
            if (formDef.formData) {
              formData = formDef.formData;
            } else {
              // Otherwise use the whole object
              formData = formDef;
            }

            // Process fields according to requirements for offline mode
            if (formData.fields) {
              formData.fields = processOfflineFormFields(formData.fields);
            }

            setTitle('Create Cultivation (Offline)');
            setOfflineMode(true);
          } else {
            console.error('No cultivation form definition found in local DB');
            setData({});
            toast.error(
              'No offline cultivation form data available. Please download when online.'
            );
            setLoading(false);
            return;
          }
        }
      } else if (edit) {
        // EDIT MODE
        try {
          // Try to get cultivation from offline DB first
          const offlineCultivation = await offlineDBService.getCultivationById(
            parseInt(id, 10) || id
          );

          if (offlineCultivation && !isOnline) {
            // We have an offline cultivation and we're offline
            console.log('Editing offline cultivation:', offlineCultivation);
            setCultivationData(offlineCultivation);

            // Get the form definition from offline DB
            const formDef = await offlineDBService.getFormDefinition(
              'cultivation-create'
            );

            if (formDef) {
              // Extract formData if it exists
              if (formDef.formData) {
                formData = formDef.formData;
              } else {
                formData = formDef;
              }

              // Process fields according to requirements for offline mode
              if (formData.fields) {
                formData.fields = processOfflineFormFields(formData.fields);
              }

              // Populate the form with cultivation data
              formData = populateFormWithCultivationData(
                formData,
                offlineCultivation
              );
              setTitle('Edit Cultivation (Offline)');
              setOfflineMode(true);
            } else {
              toast.error('Could not find cultivation form definition offline');
              setLoading(false);
              return;
            }
          } else {
            // Either we're online or the cultivation isn't in offline DB
            formResponse = await cultivationApi.getEditCultivationForm(
              farmId,
              true, // edit:true
              id,
              false // view:false
            );
            formData = formResponse?.data?.results;
            setTitle(formData?.form?.title || 'Edit Cultivation');
            setOfflineMode(false);
          }
        } catch (error) {
          console.error('Error fetching cultivation for edit:', error);

          if (!isOnline) {
            // If offline and error, try to fall back to form definition
            const formDef = await offlineDBService.getFormDefinition(
              'cultivation-create'
            );
            if (formDef) {
              // Extract formData if it exists
              if (formDef.formData) {
                formData = formDef.formData;
              } else {
                formData = formDef;
              }

              // Process fields according to requirements for offline mode
              if (formData.fields) {
                formData.fields = processOfflineFormFields(formData.fields);
              }

              setTitle('Edit Cultivation (Offline)');
              setOfflineMode(true);
              toast.warning(
                'Could not find cultivation data. Form may be empty.'
              );
            } else {
              toast.error('Could not load cultivation form offline');
              setLoading(false);
              return;
            }
          } else {
            toast.error('Failed to load cultivation data for editing');
            setLoading(false);
            return;
          }
        }
      } else if (view) {
        // VIEW MODE
        try {
          // Try to get from offline DB first
          const offlineCultivation = await offlineDBService.getCultivationById(
            parseInt(id, 10) || id
          );

          if (offlineCultivation && !isOnline) {
            // We have an offline cultivation and we're offline
            console.log('Viewing offline cultivation:', offlineCultivation);
            setCultivationData(offlineCultivation);

            // Get the form definition from offline DB
            const formDef = await offlineDBService.getFormDefinition(
              'cultivation-create'
            );

            if (formDef) {
              // Extract formData if it exists
              if (formDef.formData) {
                formData = formDef.formData;
              } else {
                formData = formDef;
              }

              // Process fields according to requirements for offline mode
              if (formData.fields) {
                formData.fields = processOfflineFormFields(formData.fields);
              }

              // Populate the form with cultivation data
              formData = populateFormWithCultivationData(
                formData,
                offlineCultivation
              );
              setTitle('View Cultivation (Offline)');
              setOfflineMode(true);
            } else {
              toast.error('Could not find cultivation form definition offline');
              setLoading(false);
              return;
            }
          } else {
            // Either we're online or the cultivation isn't in offline DB
            formResponse = await cultivationApi.getEditCultivationForm(
              farmId,
              false, // edit:false
              id,
              true // view:true
            );
            formData = formResponse?.data?.results;
            setTitle(formData?.form?.title || 'View Cultivation');
            setOfflineMode(false);
          }
        } catch (error) {
          console.error('Error fetching cultivation for view:', error);
          toast.error('Failed to load cultivation data for viewing');
          setLoading(false);
          return;
        }
      }

      // Now that we have the form data, we need to enhance the farm dropdown
      // by adding options from offline farms
      if (formData && formData.fields) {
        // Find the farm dropdown field
        const farmFieldIndex = formData.fields.findIndex(
          field => field.id === 'farm'
        );

        if (farmFieldIndex !== -1) {
          try {
            // Get all farms from local DB
            const offlineFarms = await offlineDBService.getAllFarms();
            console.log(`Fetched ${offlineFarms.length} farms from offline DB`);

            // Store the existing options or initialize an empty array
            const farmField = formData.fields[farmFieldIndex];
            const existingOptions = Array.isArray(farmField.options)
              ? [...farmField.options]
              : [];
            console.log(
              `Found ${existingOptions.length} existing farm options`
            );

            // Create a set of existing farm IDs to avoid duplicates
            const existingFarmIds = new Set(
              existingOptions.map(option => option.value.toString())
            );

            // Create new options for offline farms
            const offlineFarmOptions = offlineFarms
              .filter(farm => {
                // Skip if this farm is already in the existing options
                return !existingFarmIds.has(farm.id.toString());
              })
              .map(farm => {
                // Format name based on available fields
                const farmName =
                  farm.name || farm.farm_name || `Farm ${farm.id}`;

                // Format area if available
                const areaText = farm.area
                  ? ` (${farm.area} ${farm.area_unit || 'acres'})`
                  : '';

                return {
                  // Use farm.id as the value
                  value: farm.id.toString(),
                  // Format label with name and area if available
                  label: `${farmName}${areaText}`
                };
              });

            console.log(
              `Adding ${offlineFarmOptions.length} offline farms to dropdown`
            );

            // Merge existing options with offline farm options
            formData.fields[farmFieldIndex].options = [
              ...existingOptions,
              ...offlineFarmOptions
            ];

            console.log(
              `Updated farm dropdown now has ${formData.fields[farmFieldIndex].options.length} options`
            );

            // If we're in create mode and have a farmId from the URL,
            // set it as the default value for the farm field
            if (!edit && !view && farmId) {
              console.log(
                `Setting default farm ID to ${farmId} from URL parameter`
              );
              formData.fields[farmFieldIndex].defaultValue = farmId.toString();
            }
          } catch (farmsError) {
            console.error('Error fetching farms from offline DB:', farmsError);
            // Continue with the original form data if we fail to add offline farms
          }
        } else {
          console.warn('Farm dropdown field not found in form definition');
        }
      }

      // Set the final form data
      setData(formData);
    } catch (error) {
      console.error('Error in fetchFormData:', error);
      toast.error('Failed to load cultivation form');
    } finally {
      setLoading(false);
    }
  };

  // Function to populate form fields with cultivation data for edit/view mode
  const populateFormWithCultivationData = (formSchema, cultivationData) => {
    if (!formSchema || !cultivationData) {
      console.error('Missing required data to populate form');
      return formSchema;
    }

    // Clone the form schema to avoid modifying the original
    const updatedForm = JSON.parse(JSON.stringify(formSchema));

    if (!updatedForm.fields || !Array.isArray(updatedForm.fields)) {
      console.error('No fields found in form schema or fields is not an array');
      return updatedForm;
    }

    // Update form fields with cultivation data
    updatedForm.fields = updatedForm.fields.map(field => {
      // Skip if no data for this field
      if (cultivationData[field.id] === undefined) {
        return field;
      }

      // Get the value from cultivation data
      const fieldValue = cultivationData[field.id];

      // Return field with updated defaultValue
      return {
        ...field,
        defaultValue: fieldValue
      };
    });

    return updatedForm;
  };

  useEffect(() => {
    fetchFormData();
  }, [tenant, edit, view, isOnline]);

  const handleFormSubmit = async formData => {
    try {
      // Format the form data (add any required fields)
      let formattedData = {
        ...formData,
        farmer_name: '',
        farm_name: '',
        unutilized_area: '',
        farm_area: ''
      };

      if (isOnline) {
        // Online submission
        if (edit) {
          // Edit existing cultivation
          const response = await cultivationApi.updateCultivationReport(
            id,
            formattedData
          );
          formSubmissionToast(response, toast, 'Cultivation', edit);

          // If we have a local offline version, delete it
          try {
            const localCultivation = await offlineDBService.getCultivationById(
              id
            );
            if (localCultivation) {
              await offlineDBService.deleteCultivation(id);
              console.log(
                `Removed local cultivation ${id} after successful online update`
              );
            }
          } catch (dbError) {
            console.error(
              'Error handling local cultivation after update:',
              dbError
            );
          }
        } else {
          // Create new cultivation
          const response = await cultivationApi.submitCultivation(
            formattedData,
            farmId
          );
          formSubmissionToast(response, toast, 'Cultivation', edit);
        }

        // Navigate back to cultivations list
        navigate(`/${tenant}/cultivationReport/`);
      } else {
        // Offline submission
        console.log('Device is offline, saving cultivation locally');

        if (edit && id) {
          // Update existing cultivation in IndexedDB
          await offlineDBService.updateCultivation(id, formattedData);
          toast.success('Cultivation updated locally. Will sync when online.');
        } else {
          // Create new cultivation in IndexedDB
          await offlineDBService.saveCultivation(formattedData);
          toast.success('Cultivation saved locally. Will sync when online.');
        }

        // Navigate to offline data page
        navigate(`/offline-data/`);
      }
    } catch (err) {
      console.error('Error submitting cultivation form:', err);

      if (!isOnline) {
        // If offline, try to save to local DB regardless of the error
        try {
          if (edit && id) {
            await offlineDBService.updateCultivation(id, formData);
            toast.warning(
              'Error occurred but data saved locally for later sync.'
            );
            navigate(`/offline-data/`);
          } else {
            await offlineDBService.saveCultivation(formData);
            toast.warning(
              'Error occurred but data saved locally for later sync.'
            );
            navigate(`/offline-data/`);
          }
        } catch (dbError) {
          console.error('Error saving cultivation locally:', dbError);
          toast.error('Failed to save cultivation data locally.');
        }
      } else {
        // If online, show the API error
        formSubmissionToast(err, toast, 'Cultivation', edit);
      }
    }
  };

  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() =>
          offlineMode
            ? navigate(`/offline-data`)
            : navigate(`/${tenant}/cultivationReport`)
        }
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>{title}</span>
    </div>
  );

  return (
    <Container>
      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading cultivation form...</p>
              </div>
            ) : !isEmpty(data) ? (
              <DynamicForm
                data={data}
                header={true}
                onSubmit={handleFormSubmit}
                view={view}
              />
            ) : (
              <div className="text-center p-5">
                <p>No cultivation form data available.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
};

CultivationForm.propTypes = {
  edit: PropTypes.bool,
  view: PropTypes.bool
};

export default CultivationForm;
